import {CheckoutStatus} from '@lookiero/checkout';
import {CheckoutProjection} from '@lookiero/checkout/dist/src/projection/checkout/checkout';
import {Order, OrderStatusType} from '@domain/model/Order';
import {MY_AREA_PATH, MyAreaPathType} from '@modules/routing/routes.definition';
import {getCurrentOrders} from '@views/Account/_hooks';

export interface MyAreaOverallInfo {
  readonly orders?: Order[];
  readonly subscription?: {readonly id?: string};
  readonly WIPOrder?: boolean;
  readonly checkout?: CheckoutProjection;
}

export interface MyAreaClassification {
  readonly holding: boolean;
  readonly path: MyAreaPathType;
}

const STATUS_TO_PATH: Partial<Record<OrderStatusType, MyAreaPathType>> = {
  EN_MARCHA: MY_AREA_PATH.PLACED,
  PEDIDO_REALIZADO: MY_AREA_PATH.PLACED,
  BORRADOR: MY_AREA_PATH.PLACED,
  EN_PREPARACION: MY_AREA_PATH.PLACED,
  ENVIADO: MY_AREA_PATH.TRACKING,
  LIQUIDADO: MY_AREA_PATH.ORDER,
  RETORNADO: MY_AREA_PATH.ORDER,
  COMPLETADO: MY_AREA_PATH.ORDER,
};

// The delivered box no longer carries a dedicated legacy status (ENTREGADO removed): it is identified by
// joining on `boxId` against the customer's first available checkout (UUID === UUID).
const CHECKOUT_PREVIEW_STATUSES: ReadonlyArray<CheckoutStatus> = [CheckoutStatus.AVAILABLE, CheckoutStatus.NOTIFIED];

const selectActiveOrder = (orders: Order[], checkout?: CheckoutProjection): Order | undefined => {
  // The checkout-joined (delivered) order must WIN over `orders[0]`: in a multi-order state the first
  // entry would otherwise drive the classification and route away from the checkout preview.
  const orderDelivered =
    checkout?.boxId != null ? orders.find(({services: [order]}) => order?.boxId === checkout.boxId) : undefined;

  return orderDelivered ?? orders[0];
};

const isPreOrderState = ({orders = [], subscription}: MyAreaOverallInfo): boolean =>
  !subscription || orders.length === 0;

const hasCheckoutPreview = (orders: Order[], checkout?: CheckoutProjection): boolean =>
  Boolean(
    checkout &&
    CHECKOUT_PREVIEW_STATUSES.includes(checkout.status) &&
    getCurrentOrders(orders, checkout).inProgress !== undefined,
  );

const classifyMyAreaState = ({
  orders = [],
  subscription,
  WIPOrder,
  checkout,
}: MyAreaOverallInfo): MyAreaClassification => {
  const path = ((): MyAreaPathType => {
    if (isPreOrderState({orders, subscription})) {
      return MY_AREA_PATH.ORDER;
    }

    if (hasCheckoutPreview(orders, checkout)) {
      return MY_AREA_PATH.CHECKOUT_PREVIEW;
    }

    const activeOrder = selectActiveOrder(orders, checkout);
    const orderStatus = activeOrder?.services[0]?.status;

    return (orderStatus && STATUS_TO_PATH[orderStatus]) || MY_AREA_PATH.ORDER;
  })();

  return {holding: Boolean(WIPOrder), path};
};

export {classifyMyAreaState, selectActiveOrder, isPreOrderState, hasCheckoutPreview};
